import axiosInstance from '../axiosConfig';
import {
  AdminDashboardResponse,
  NonMemberAnalyticsResponse,
  ConversionFunnelResponse,
  GuestPassAnalyticsResponse,
  ActivityReportResponse,
  UpcomingAnniversaryResetsResponse,
} from '../../types';

/**
 * Admin Analytics Service
 * Handles admin dashboard, analytics, and bulk operations API calls
 */
class AdminAnalyticsService {
  private baseUrl = '/admin';

  // ============================================
  // Dashboard & Analytics
  // ============================================

  /**
   * Get admin dashboard overview
   */
  async getDashboard(): Promise<AdminDashboardResponse> {
    const response = await axiosInstance.get(`${this.baseUrl}/dashboard`);
    return response.data;
  }

  /**
   * Get non-member analytics
   */
  async getNonMemberAnalytics(): Promise<NonMemberAnalyticsResponse> {
    const response = await axiosInstance.get(`${this.baseUrl}/non-members/analytics`);
    return response.data;
  }

  /**
   * Get conversion funnel analytics
   */
  async getConversionFunnel(params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<ConversionFunnelResponse> {
    const response = await axiosInstance.get(`${this.baseUrl}/conversion-funnel`, { params });
    return response.data;
  }

  /**
   * Get guest pass analytics
   */
  async getGuestPassAnalytics(params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<GuestPassAnalyticsResponse> {
    const response = await axiosInstance.get(`${this.baseUrl}/guest-passes/analytics`, { params });
    return response.data;
  }

  /**
   * Get activity report
   */
  async getActivityReport(days: number = 30): Promise<ActivityReportResponse> {
    const response = await axiosInstance.get(`${this.baseUrl}/activity-report`, {
      params: { days },
    });
    return response.data;
  }

  // ============================================
  // Non-Member Management
  // ============================================

  /**
   * Block a non-member
   */
  async blockNonMember(
    memberId: string,
    reason?: string
  ): Promise<{ success: boolean; message: string; data: { id: string; membership_status: string; blocked_at: string } }> {
    const response = await axiosInstance.post(`${this.baseUrl}/non-members/${memberId}/block`, { reason });
    return response.data;
  }

  /**
   * Unblock a non-member
   */
  async unblockNonMember(
    memberId: string
  ): Promise<{ success: boolean; message: string; data: { id: string; membership_status: string; unblocked_at: string } }> {
    const response = await axiosInstance.post(`${this.baseUrl}/non-members/${memberId}/unblock`);
    return response.data;
  }

  /**
   * Update a non-member's status (active/inactive)
   */
  async updateStatus(
    memberId: string,
    status: 'NON_MEMBER_ACTIVE' | 'NON_MEMBER_INACTIVE'
  ): Promise<{ success: boolean; message: string; data: { id: string; membership_status: string; updated_at: string } }> {
    const response = await axiosInstance.put(`${this.baseUrl}/non-members/${memberId}/status`, { status });
    return response.data;
  }

  /**
   * Update interest level for a non-member
   */
  async updateInterestLevel(
    memberId: string,
    interestLevel: string,
    notes?: string
  ): Promise<{ success: boolean; message: string; data: { id: string; conversion_interest_level: string; conversion_notes?: string } }> {
    const response = await axiosInstance.put(`${this.baseUrl}/non-members/${memberId}/interest`, {
      interest_level: interestLevel,
      notes,
    });
    return response.data;
  }

  /**
   * Bulk update interest levels
   */
  async bulkUpdateInterestLevels(
    updates: Array<{ member_id: string; interest_level: string; notes?: string }>
  ): Promise<{
    success: boolean;
    total: number;
    successful: number;
    failed: number;
    results: Array<{ member_id: string; success: boolean; error?: string }>;
  }> {
    const response = await axiosInstance.post(`${this.baseUrl}/non-members/bulk-update-interest`, { updates });
    return response.data;
  }

  // ============================================
  // Annual Guest Visit Reset
  // ============================================

  /**
   * Reset all guest visits (January 1st reset)
   */
  async resetAllGuestVisits(): Promise<{
    success: boolean;
    message: string;
    data: {
      total_non_members: number;
      reset_count: number;
      reset_date: string;
      errors?: Array<{ member_id: string; error: string }>;
    };
  }> {
    const response = await axiosInstance.post(`${this.baseUrl}/guest-visits/reset-all`);
    return response.data;
  }

  /**
   * Reset guest visits by anniversary (daily job)
   */
  async resetGuestVisitsByAnniversary(): Promise<{
    success: boolean;
    message: string;
    data: {
      checked_with_acquisition_date: number;
      reset_count: number;
      reset_date: string;
      errors?: Array<{ member_id: string; error: string }>;
    };
  }> {
    const response = await axiosInstance.post(`${this.baseUrl}/guest-visits/reset-anniversaries`);
    return response.data;
  }

  /**
   * Reset guest visits for a specific non-member (manual override)
   */
  async resetGuestVisitsForMember(memberId: string): Promise<{
    success: boolean;
    message: string;
    data: {
      id: string;
      previous_count: number;
      new_count: number;
      reset_date: string;
    };
  }> {
    const response = await axiosInstance.post(`${this.baseUrl}/non-members/${memberId}/reset-guest-visits`);
    return response.data;
  }

  /**
   * Get upcoming anniversary resets
   */
  async getUpcomingAnniversaryResets(daysAhead: number = 7): Promise<UpcomingAnniversaryResetsResponse> {
    const response = await axiosInstance.get(`${this.baseUrl}/guest-visits/upcoming-anniversaries`, {
      params: { days_ahead: daysAhead },
    });
    return response.data;
  }

  // ============================================
  // Export Functions
  // ============================================

  /**
   * Export non-members to CSV
   */
  async exportNonMembersCSV(filters?: {
    status?: string;
    acquisition_source?: string;
    interest_level?: string;
  }): Promise<Blob> {
    const response = await axiosInstance.get(`${this.baseUrl}/export/non-members`, {
      params: filters,
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Export guest passes to CSV
   */
  async exportGuestPassesCSV(params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<Blob> {
    const response = await axiosInstance.get(`${this.baseUrl}/export/guest-passes`, {
      params,
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Export applications to CSV
   */
  async exportApplicationsCSV(status?: string): Promise<Blob> {
    const response = await axiosInstance.get(`${this.baseUrl}/export/applications`, {
      params: status ? { status } : undefined,
      responseType: 'blob',
    });
    return response.data;
  }

  // ============================================
  // Helper: Download CSV
  // ============================================

  /**
   * Helper to trigger CSV download
   */
  downloadCSV(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
}

// Export singleton instance
export const adminAnalyticsService = new AdminAnalyticsService();
export default adminAnalyticsService;
